import React, { useState, useRef, useEffect, useCallback } from 'react';
import parse, { domToReact } from 'html-react-parser';
import { useSelector } from 'react-redux';
import './DexnovaChatbot.css';
import {
	getConversationsFromCookie,
	addConversationToCookie,
	getConversationById,
	deleteConversationById,
	createConversation,
	formatRelativeTime
} from './cookieUtils';

// Constants
const WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL;
const LAST_CONVERSATION_KEY = 'dexnova_last_conversation_id';

// Debug: Log environment variables on load
console.log('DexNova: Environment check:', {
	WEBHOOK_URL,
	REACT_APP_DEX_ENV: process.env.REACT_APP_DEX_ENV,
	REACT_APP_BASE_URL: process.env.REACT_APP_BASE_URL,
	allEnvVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
});

const INITIAL_MESSAGE = {
	sender: 'bot',
	text: 'Hi there! I am DexNova. How can I assist you today?'
};

const DexnovaChatbot = () => {
	// Redux state
	const user = useSelector(state => state.auth.user);

	// React state - UI control
	const [isCollapsed, setIsCollapsed] = useState(true);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [isMinimized, setIsMinimized] = useState(false);
	const [isTyping, setIsTyping] = useState(false);
	const [showHistory, setShowHistory] = useState(false);

	// React state - Chat data
	const [messages, setMessages] = useState([INITIAL_MESSAGE]);
	const [input, setInput] = useState('');
	const [conversations, setConversations] = useState([]);
	const [currentConversationId, setCurrentConversationId] = useState(null);

	// Refs
	const messagesEndRef = useRef(null);
	const inputRef = useRef(null);

	// Generate session key once
	const [sessionKey, setSessionKey] = useState(() => crypto.randomUUID());

	// Helper functions
	const sanitizeHTML = (html) => {
		if (!html) return '';
		// Remove script tags
		let out = String(html).replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
		// Remove inline event handlers like onerror, onclick, etc.
		out = out.replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '')
				 .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '');
		return out;
	};

	const formatBotReply = useCallback((text) => {
		if (!text) return '';
		
		let formattedText = text;

		// First, clean up any existing malformed HTML before processing markdown
		formattedText = formattedText.replace(/<a href="<a href="/g, '<a href="');
		formattedText = formattedText.replace(/"><\/a>/g, '">');
		formattedText = formattedText.replace(/"\s*target="_blank"\s*rel="[^"]*"\s*class="[^"]*">/g, '">');

		// Clean up nested or malformed anchor tags
		formattedText = formattedText.replace(
			/<a href="([^"]+)"\s*target="_blank"\s*rel="[^"]*"\s*class="[^"]*">([^<]+)<\/a>\s*target="_blank"\s*rel="[^"]*"\s*class="[^"]*">([^<]+)<\/a>/g, 
			'<a href="$1" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">$3</a>'
		);

		// Check if the entire response is JSON and format it nicely
		if (formattedText.trim().startsWith('{') && formattedText.trim().endsWith('}')) {
			try {
				const parsed = JSON.parse(formattedText);
				return `<div className="bg-gray-100 p-3 rounded-lg my-2 overflow-x-auto border border-gray-200"><pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">${JSON.stringify(parsed, null, 2)}</pre></div>`;
			} catch (e) {
				console.warn('DexNova: invalid JSON in bot reply root', e);
			}
		}

		// Remove markdown code blocks
		formattedText = formattedText.replace(/```[\w]*\n?([\s\S]*?)```/g, (match, content) => {
			if (match.includes('json')) {
				try {
					const parsed = JSON.parse(content);
					return `<div className="bg-gray-100 p-3 rounded-lg my-2 overflow-x-auto border border-gray-200"><pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">${JSON.stringify(parsed, null, 2)}</pre></div>`;
				} catch {
					return `<div className="bg-gray-100 p-3 rounded-lg my-2 overflow-x-auto border border-gray-200"><pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">${content}</pre></div>`;
				}
			}
			return `<div className="bg-gray-100 p-3 rounded-lg my-2 overflow-x-auto border border-gray-200"><pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">${content}</pre></div>`;
		});
		
		// Convert markdown to HTML
		formattedText = formattedText.replace(/^#{1,6}\s+(.+)$/gm, '<strong>$1</strong>');
		formattedText = formattedText.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">$1</a>');
		formattedText = formattedText.replace(/`([^`]+)`/g, '<code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">$1</code>');
		formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
		formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
		formattedText = formattedText.replace(/\n/g, '<br/>');
		
		// Sanitize before injecting into the DOM (security hotspot mitigation)
		return sanitizeHTML(formattedText);
	}, []);
	// Safely render sanitized HTML using a restrictive transform
	const renderBotContent = useCallback((text) => {
		const html = formatBotReply(text);
		const options = {
			replace: (domNode) => {
				if (!domNode || !domNode.name) return undefined;
				const name = String(domNode.name).toLowerCase();
				// Drop unsafe tags entirely
				if (["script", "style", "iframe", "object", "embed"].includes(name)) return null;
				// Void elements must not have children
				const voidTags = new Set(["area","base","br","col","embed","hr","img","input","link","meta","param","source","track","wbr"]);
				const attribs = domNode.attribs || {};
				const safeAttribs = {};
				Object.keys(attribs).forEach((k) => {
					const lower = k.toLowerCase();
					if (!lower.startsWith('on') && lower !== 'style') {
						// Normalize 'class' to 'className'
						safeAttribs[lower === 'class' ? 'className' : k] = attribs[k];
					}
				});
				if (name === 'a') {
					let href = attribs.href || '#';
					if (!/^(https?:|mailto:)/i.test(href)) href = '#';
					// Remove href from safeAttribs to avoid overwriting our sanitized version
					const { href: _, ...attribsWithoutHref } = safeAttribs;
					return (
						<a href={href} target="_blank" rel="noopener noreferrer" {...attribsWithoutHref}>
							{domToReact(domNode.children, options)}
						</a>
					);
				}
				if (voidTags.has(name)) {
					return React.createElement(name, safeAttribs);
				}
				return React.createElement(name, safeAttribs, domNode.children ? domToReact(domNode.children, options) : null);
			}
		};
		return parse(html, options);
	}, [formatBotReply]);

	const scrollToBottom = useCallback(() => {
		setTimeout(() => {
			messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
		}, 100);
	}, []);

	// UI state management functions
	const toggleCollapse = useCallback(() => {
		setIsCollapsed(prev => {
			const next = !prev;
			// When opening (next = false), focus the input after animation
			if (!next) {
				setTimeout(() => {
					inputRef.current?.focus();
				}, 300);
			}
			return next;
		});
		setIsFullscreen(false);
		setIsMinimized(false);
		setShowHistory(false);
	}, []);

	const toggleFullscreen = useCallback(() => {
		setIsFullscreen(prev => !prev);
		setIsCollapsed(false);
		setIsMinimized(false);
		setShowHistory(false);
		setTimeout(() => {
			inputRef.current?.focus();
		}, 300);
	}, []);

	const toggleMinimize = useCallback(() => {
		setIsMinimized(prev => {
			const next = !prev;
			// When restoring from minimized (next = false), focus input
			if (!next) {
				setTimeout(() => {
					inputRef.current?.focus();
				}, 300);
			}
			return next;
		});
		setIsCollapsed(false);
		setIsFullscreen(false);
		setShowHistory(false);
	}, []);

	const toggleHistory = useCallback(() => {
		setShowHistory(prev => !prev);
	}, []);

	// Event handlers
	const handleGlobalKeyDown = useCallback((event) => {
		if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'D') {
			event.preventDefault();
			toggleCollapse();
		}
	}, [toggleCollapse]);

	// Conversation management functions
	const loadConversations = useCallback(() => {
		const savedConversations = getConversationsFromCookie();
		setConversations(savedConversations);
	}, []);

	const loadConversation = useCallback((conversationId) => {
		const conversation = getConversationById(conversationId);
		if (conversation) {
			setMessages(conversation.messages);
			setCurrentConversationId(conversation.id);
			setSessionKey(conversation.id);
			setShowHistory(false);
			try { localStorage.setItem(LAST_CONVERSATION_KEY, conversation.id); } catch (e) { console.warn('DexNova: failed to persist last conversation id', e); }
		}
	}, []);

	const startNewConversation = useCallback(() => {
		// Generate a fresh session/conversation id and apply it immediately
		const nextKey = crypto.randomUUID();
		setMessages([INITIAL_MESSAGE]);
		setCurrentConversationId(nextKey);
		setSessionKey(nextKey);
		setShowHistory(false);
		setInput('');
		try { localStorage.setItem(LAST_CONVERSATION_KEY, nextKey); } catch (e) { console.warn('DexNova: failed to set next conversation id', e); }

		// Immediately create and persist a new conversation so it appears in history
		try {
			const conversation = createConversation([INITIAL_MESSAGE], nextKey);
			addConversationToCookie(conversation);
			loadConversations();
		} catch (e) {
			console.warn('Failed to create new conversation on start:', e);
		}
	}, [loadConversations]);

	const deleteConversation = useCallback((conversationId, e) => {
		if (e && e.stopPropagation) e.stopPropagation();
		deleteConversationById(conversationId);
		// If the deleted conversation was marked as last active, clear it
		try {
			const lastId = localStorage.getItem(LAST_CONVERSATION_KEY);
			if (lastId === conversationId) {
				localStorage.removeItem(LAST_CONVERSATION_KEY);
			}
		} catch (e) { console.warn('DexNova: failed to clear last conversation on delete', e); }
		loadConversations();
		// If we deleted the currently open conversation
		if (conversationId === currentConversationId) {
			if (showHistory) {
				// Stay in the Your Conversations page; clear selection and reset preview
				setCurrentConversationId(null);
				setMessages([INITIAL_MESSAGE]);
				// Do NOT start a new conversation or navigate away from history
			} else {
				// When not in history view, keep current behavior and start a fresh chat
				startNewConversation();
			}
		}
	}, [currentConversationId, showHistory, loadConversations, startNewConversation]);

	// Chat functionality
	const sendMessage = useCallback(async () => {
		if (!input.trim()) return;
		
		// If no user is logged in, show a message but still allow conversation saving
		if (!user) {
			const userMessage = input.trim();
			const newUserMessage = { sender: 'user', text: userMessage };
			
			// Clear input immediately after sending
			setInput('');
			if (inputRef.current) {
				inputRef.current.style.height = 'auto';
				inputRef.current.style.overflowY = 'hidden';
			}
			
			// Add a bot response indicating login is needed for AI responses
			const botMessage = { 
				sender: 'bot', 
				text: 'Please log in to get AI responses from DexNova. However, your conversation history will still be saved!' 
			};
			
			// Update messages with both user and bot messages
			setMessages(prev => [...prev, newUserMessage, botMessage]);
			scrollToBottom();
			return;
		}

		const userMessage = input.trim();
		const newUserMessage = { sender: 'user', text: userMessage };
		setMessages(prev => [...prev, newUserMessage]);
		
		// Clear input immediately after sending
		setInput('');
		// Reset the input area height after sending
		if (inputRef.current) {
			inputRef.current.style.height = 'auto';
			inputRef.current.style.overflowY = 'hidden';
		}
		
		setIsTyping(true);

		try {
			// Debug: Check if webhook URL is defined
			if (!WEBHOOK_URL) {
				console.error('DexNova: WEBHOOK_URL is undefined!');
				console.error('DexNova: Available env vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));
				throw new Error('Webhook URL is not configured. Please check your environment variables.');
			}
			
			console.log('DexNova: Sending request to:', WEBHOOK_URL);
			console.log('DexNova: Payload:', {
				chatInput: userMessage,
				session_key: sessionKey,
				user_id: user.id,
				timestamp: new Date().toISOString()
			});
			
			const res = await fetch(WEBHOOK_URL, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					chatInput: userMessage,
					session_key: sessionKey,
					user_id: user.id,
					timestamp: new Date().toISOString(),
					debug: true
				}), 
			});

			console.log('DexNova: Response status:', res.status, res.statusText);
			
			if (!res.ok) {
				const errorText = await res.text();
				console.error('DexNova: HTTP Error Response:', errorText);
				throw new Error(`HTTP ${res.status}: ${res.statusText}`);
			}
			
			const text = await res.text();
			console.log('DexNova: Response text:', text);
			const data = text ? JSON.parse(text) : {};
			const replyText = data?.reply || 'Sorry, I didn\'t get that.';
			
			const botMessage = { sender: 'bot', text: replyText };
			setMessages(prev => [...prev, botMessage]);
		} catch (err) {
			console.error('DexNova Chat Error:', err);
			console.error('DexNova Error Details:', {
				name: err.name,
				message: err.message,
				stack: err.stack
			});
			
			let errorMessage = 'Something went wrong.';
			
			// Provide more specific error messages
			if (err.message.includes('Webhook URL is not configured')) {
				errorMessage = '⚠️ Configuration Error: Webhook URL is missing. Please restart the dev server.';
			} else if (err.message.includes('Failed to fetch') || err.name === 'TypeError') {
				errorMessage = '🌐 Network Error: Cannot reach the AI service. Check your connection or the n8n webhook URL.';
			} else if (err.message.includes('HTTP')) {
				errorMessage = `🔴 Server Error: ${err.message}. The n8n workflow may not be active.`;
			} else {
				errorMessage = `❌ Error: ${err.message}`;
			}
			
			setMessages(prev => [...prev, { sender: 'bot', text: errorMessage }]);
		}

		setIsTyping(false);
		scrollToBottom();
	}, [input, user, sessionKey, scrollToBottom]);

	const handleKeyDown = useCallback((e) => {
		// Enter sends; Shift+Enter creates a new line
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	}, [sendMessage]);

	// Effects
	useEffect(() => {
		document.addEventListener('keydown', handleGlobalKeyDown);
		scrollToBottom();
		loadConversations(); // Load conversations on component mount
		
		return () => {
			document.removeEventListener('keydown', handleGlobalKeyDown);
		};
	}, [handleGlobalKeyDown, scrollToBottom, loadConversations]);

	// Auto-scroll when messages change
	useEffect(() => {
		scrollToBottom();
	}, [messages, scrollToBottom]);

	// Refresh conversations list whenever history panel is opened
	useEffect(() => {
		if (showHistory) {
			loadConversations();
		}
	}, [showHistory, loadConversations]);

	// Auto-load the last active (or most recent) conversation after conversations are loaded
	useEffect(() => {
		// If the history panel is open, do not auto-navigate away from it
		if (showHistory) return;
		if (!conversations || conversations.length === 0) return;
		// If a conversation is already selected (including a brand-new one), do nothing
		if (currentConversationId) return;
		// If messages already contain history, do nothing
		if (messages && messages.length > 1) return;
		let targetId = null;
		try {
			const lastId = localStorage.getItem(LAST_CONVERSATION_KEY);
			if (lastId && conversations.some(c => c.id === lastId)) {
				targetId = lastId;
			} else {
				const latest = [...conversations].sort((a, b) => new Date(b.lastUpdated || b.timestamp) - new Date(a.lastUpdated || a.timestamp))[0];
				if (latest) targetId = latest.id;
			}
		} catch (e) {
			console.warn('Failed to determine last conversation to auto-load:', e);
		}
		if (targetId) loadConversation(targetId);
	}, [conversations, messages, currentConversationId, showHistory, loadConversation]);

	// Persist conversations to cookies whenever messages change and we have user input
	useEffect(() => {
		// Skip initial render with only the greeting message
		if (!messages || messages.length <= 1) return;
		const hasUserMessages = messages.some(m => m.sender === 'user');
		if (!hasUserMessages) return;

		try {
			const conversation = createConversation(messages, currentConversationId || sessionKey);
			addConversationToCookie(conversation);
			setCurrentConversationId(conversation.id);
			loadConversations();
			try { localStorage.setItem(LAST_CONVERSATION_KEY, conversation.id); } catch (e) { console.warn('DexNova: failed to persist conversation id', e); }
		} catch (e) {
			console.warn('Failed to persist conversation:', e);
		}
	}, [messages, currentConversationId, sessionKey, loadConversations]);

	return (
			<>
				{/* Floating Toggle Button */}
				<div className="chatbot-toggle-container">
					<div className="chatbot-toggle-wrapper">
						{isCollapsed && (
							<div className="chatbot-toggle-label">
								<span className="chatbot-toggle-text">Ask DexNova</span>
							</div>
						)}
						<button onClick={toggleCollapse} className="chatbot-toggle-button chatbot-float" aria-label={isCollapsed ? 'Open DexNova AI chat' : 'Close DexNova AI chat'}>
							<div className="chatbot-toggle-overlay" />
							<div className="chatbot-toggle-icon">
								<svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									{isCollapsed ? (
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
									) : (
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									)}
								</svg>
							</div>
						</button>
					</div>
				</div>

				{/* Chat UI */}
				<div className={`chatbot-container ${isCollapsed ? 'chatbot-collapsed' : isFullscreen ? 'chatbot-fullscreen' : isMinimized ? 'chatbot-minimized' : 'chatbot-expanded'}`}>
					<div className="chatbot-window">
						{/* Header */}
						<div className="chatbot-header">
							<div className="chatbot-header-info">
								<div className="chatbot-avatar-container">
									<div className="chatbot-avatar">
										<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
										</svg>
									</div>
								</div>
								<div className="chatbot-info">
									<h3 className="chatbot-title">DexNova AI</h3>
									<p className="chatbot-subtitle">A Buildex Beta Tool</p>
								</div>
							</div>

							<div className="chatbot-controls">
								<button onClick={toggleHistory} className="chatbot-control-button" title="View conversation history" aria-label="View conversation history">
									<svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
									</svg>
								</button>
								<button onClick={toggleMinimize} className="chatbot-control-button" title={isMinimized ? 'Restore chat' : 'Minimize chat'} aria-label={isMinimized ? 'Restore DexNova AI chat' : 'Minimize DexNova AI chat'}>
									<svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
									</svg>
								</button>
								<button onClick={toggleFullscreen} className="chatbot-control-button" title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen chat'} aria-label={isFullscreen ? 'Exit fullscreen mode' : 'Enter fullscreen mode'}>
									<svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										{isFullscreen ? (
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
										) : (
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
										)}
									</svg>
								</button>
								<button onClick={toggleCollapse} className="chatbot-control-button chatbot-close-button" title="Close chat" aria-label="Close DexNova AI chat">
									<svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						{/* Conversation History or Chat Messages */}
						{showHistory ? (
							<div className="chatbot-history-container chatbot-scrollbar">
								<div className="chatbot-history-header">
									<h3 className="chatbot-history-title">YOUR CONVERSATIONS</h3>
									<button onClick={() => setShowHistory(false)} className="chatbot-history-close" title="Close history">
										<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
										</svg>
									</button>
								</div>
								<div className="chatbot-history-list">
									{conversations.length === 0 ? (
										<div className="chatbot-history-empty">
											<p>No conversations yet. Start chatting to create your first conversation!</p>
										</div>
									) : (
										conversations
											.sort((a, b) => new Date(b.lastUpdated || b.timestamp) - new Date(a.lastUpdated || a.timestamp))
											.map((conversation) => (
												<div key={conversation.id} className={`chatbot-history-item ${currentConversationId === conversation.id ? 'active' : ''}`} onClick={() => loadConversation(conversation.id)}>
													<div className="chatbot-history-item-content">
														<div className="chatbot-history-item-header">
															<div className="chatbot-history-avatar">
																<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
																</svg>
															</div>
															<span className="chatbot-history-assistant">DexNova Assistant</span>
															<span className="chatbot-history-time">{formatRelativeTime(conversation.lastUpdated || conversation.timestamp)}</span>
														</div>
														<div className="chatbot-history-preview">{conversation.title}</div>
													</div>
													<button className="chatbot-history-delete" onClick={(e) => deleteConversation(conversation.id, e)} title="Delete conversation">
														<svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
														</svg>
													</button>
												</div>
											))
									)}
								</div>
								<div className="chatbot-history-footer">
									<button className="chatbot-newchat-button" onClick={startNewConversation}>New Chat</button>
								</div>
							</div>
						) : (
							<div className="chatbot-messages chatbot-scrollbar">
								{messages.map((msg, i) => (
									<div key={i} className={`chatbot-message-row ${msg.sender === 'user' ? 'chatbot-message-user' : 'chatbot-message-bot'} chatbot-fade-in`}>
										{msg.sender === 'bot' ? (
											<div className="chatbot-message chatbot-message-bubble chatbot-message-bot-bubble">
												{renderBotContent(msg.text)}
											</div>
										) : (
											<div className="chatbot-message chatbot-message-bubble chatbot-message-user-bubble" style={{ whiteSpace: 'pre-wrap' }}>
												{msg.text}
											</div>
										)}
									</div>
								))}
								{isTyping && (
									<div className="chatbot-message-row chatbot-message-bot chatbot-fade-in">
										<div className="chatbot-message chatbot-message-bot-bubble chatbot-thinking-bubble">
											<div className="chatbot-thinking-container">
												<div className="chatbot-thinking-content">
													<span className="chatbot-thinking-text">thinking...</span>
													<div className="chatbot-typing-indicator">
														<div className="chatbot-typing-dot" style={{ animationDelay: '0ms' }} />
														<div className="chatbot-typing-dot" style={{ animationDelay: '150ms' }} />
														<div className="chatbot-typing-dot" style={{ animationDelay: '300ms' }} />
													</div>
												</div>
												<p className="chatbot-beta-message">
													I am a beta version and I am still learning, so I might take a while to think. Please be patient!
												</p>
											</div>
										</div>
									</div>
								)}
								<div ref={messagesEndRef} />
							</div>
						)}

						{/* Input Bar */}
						{!showHistory && (
							<div className="chatbot-input-container">
								<div className="chatbot-input-wrapper">
									<div className="chatbot-input-field">
										<textarea
											ref={inputRef}
											rows={1}
											className="chatbot-input"
											placeholder="Ask DexNova anything..."
											value={input}
											onChange={(e) => setInput(e.target.value)}
											onKeyDown={handleKeyDown}
											onInput={(e) => {
												const maxHeight = 150; // px
												e.target.style.height = 'auto';
												const newHeight = Math.min(e.target.scrollHeight, maxHeight);
												e.target.style.height = newHeight + 'px';
												e.target.style.overflowY = e.target.scrollHeight > maxHeight ? 'auto' : 'hidden';
											}}
											style={{ resize: 'none', overflowY: 'hidden', maxHeight: '150px' }}
										/>
								</div>
								<button onClick={sendMessage} disabled={!input.trim() || isTyping} className="chatbot-send-button" aria-label="Send message to DexNova AI" style={{ alignSelf: 'flex-end' }}>
									<span>Send</span>
								</button>
							</div>
						</div>
						)}
					</div>
				</div>
			</>
		);
};

export default DexnovaChatbot;
